// src/pages/CaseBriefPage.js
//
// Aug 2026 redesign, v3 (see project_docs/advisor_dashboard_case_brief_framework.md), reworked
// in ticket 072: ONE case brief for every client. Principle unchanged: facts, not
// interpretation. This page presents what the platform has confirmed, sourced to where it came
// from, and never tells the advisor what to do about it. Deterministic rule-engine output
// (obligations, data_gaps, data_conflicts, classification) is not interpretation and is kept.
//
// Ticket 072: the middle of the page is topic based for every client: Income on the left,
// Assets and uncommon events on the right. When get-advisor-subject sends `subject.intake`
// (the client shared a saved intake) that intake feeds the whole page and the old Snapshot /
// Calculator data is not shown (D3 = B); otherwise the old brief feeds it. The renderers live
// in src/components/advisor/brief/, the adapters in src/lib/briefSections.js. This file is data
// loading and layout only. One stable page component (rule 12): conditional JSX, no swapped types.

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'
import { useAuth } from '../context/AuthContext'
import { getAdvisorProductLabel } from '../components/HomeLink'
import { buildSections, buildIntakeSections } from '../lib/briefSections'
import { formatDate, statusLabel } from '../lib/briefFormat'
import { ADVISOR_INTAKE_NO_TAX_WORK, ADVISOR_INTAKE_UNCONFIRMED_LABEL } from '../lib/advisorIntakeModel'
import { BriefSection } from '../components/advisor/brief/BriefParts'
import { CaseHeader } from '../components/advisor/brief/CaseHeader'
import { IncomeColumn } from '../components/advisor/brief/IncomeColumn'
import { AssetsColumn } from '../components/advisor/brief/AssetsColumn'
import { TaxWorkSection } from '../components/advisor/brief/TaxWorkSection'
import { GapsCard } from '../components/advisor/brief/GapsCard'
import { CaveatsSection } from '../components/advisor/brief/CaveatsSection'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

export default function CaseBriefPage() {
  const { subjectId } = useParams()
  const navigate      = useNavigate()
  const { user }      = useAuth()

  const [subject,     setSubject]     = useState(null)
  const [profileData, setProfileData] = useState({ tp: null, yp: null })
  const [email,       setEmail]       = useState(null)
  const [phone,       setPhone]       = useState(null)
  const [triagePublicToken, setTriagePublicToken] = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [generating,  setGenerating]  = useState(false)
  const [regenerating,setRegenerating]= useState(false)
  const [error,       setError]       = useState(null)
  const [viewedSessionId, setViewedSessionId] = useState(null)
  const [viewedBrief,     setViewedBrief]     = useState(null)

  const fetchAdvisorSubject = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) throw new Error('Not authenticated')

    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-advisor-subject?subject_id=${subjectId}`,
      { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
    )
    const body = await res.json()
    if (!res.ok) throw new Error(body.error || 'Failed to load subject')
    return body.subject
  }, [subjectId])

  const loadData = useCallback(async () => {
    if (!user || !subjectId) return
    setLoading(true)
    setError(null)

    try {
      const sub = await fetchAdvisorSubject()
      setSubject(sub)

      if (sub.kind === 'lead') {
        setProfileData({ tp: sub.tp || null, yp: sub.yp || null })
        setEmail(sub.email || null)
        setPhone(sub.phone || null)
        setTriagePublicToken(sub.triage_public_token || null)
      }
    } catch (err) {
      setError(err.message || 'Subject not found.')
    }

    setLoading(false)
  }, [user, subjectId, fetchAdvisorSubject])

  useEffect(() => { loadData() }, [loadData])

  // Last visible-session fingerprint we auto-regenerated for (loop guard).
  const regenKeyRef = useRef(null)

  useEffect(() => {
    if (subject?.kind !== 'lead' || generating) return
    // Ticket 072: a client who shared an intake is shown from that intake; the old brief is
    // not generated for them (D3 = B).
    if (subject.intake) return
    if (!subject.case_brief) { generateBrief(false); return }
    // Cached brief may be stale: the default (newest visible) session can be
    // an unpublished draft that has since been edited, published or deleted.
    // Regenerating is cheap (deterministic, no model call). Loop guard:
    // regenerating does not refetch the subject, so visible_sessions can stay
    // stale and setSubject would re-fire this effect. regenKeyRef limits
    // auto-regeneration to once per observed visible[0] (id + updated_at); a
    // genuinely newer draft has a new key and regenerates again.
    const current = subject.visible_sessions?.[0] || null
    const est = subject.case_brief.calculator_estimate || null
    const stale = current
      ? (!est || est.session_id !== current.id || (current.updated_at && est.ran_at !== current.updated_at))
      : !!est
    if (!stale) return
    const key = `${subjectId}|${current?.id ?? 'none'}|${current?.updated_at ?? ''}`
    if (regenKeyRef.current === key) return
    regenKeyRef.current = key
    generateBrief(false)
  }, [subject]) // intentional: only trigger when subject changes

  async function generateBrief(isRegen = false, peekSessionId = null) {
    if (isRegen) setRegenerating(true)
    else         setGenerating(true)
    setError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Not authenticated')

      // Explicit "Refresh" re-fetches the subject itself (products/tp/yp/
      // visible_sessions) alongside regenerating the brief — see the
      // 2026-08 fix note. Skipped for peeks and the first-load
      // auto-generate (subject is already fresh there).
      const subjectRefetch = (isRegen && !peekSessionId)
        ? fetchAdvisorSubject().catch(() => null)
        : null

      const res  = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-case-brief`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            tax_subject_id: subjectId,
            ...(peekSessionId ? { calculator_session_id: peekSessionId } : {}),
          }),
        }
      )
      const json = await res.json()
      if (res.status === 403) throw new Error('Partner-data-share consent is no longer on record for this subject.')
      if (!res.ok || !json.brief) throw new Error(json.error || 'Brief generation failed')

      const refreshedSubject = subjectRefetch ? await subjectRefetch : null

      if (peekSessionId) {
        setViewedBrief(json.brief)
      } else {
        setSubject(prev => ({
          ...prev,
          ...(refreshedSubject?.kind === 'lead' ? refreshedSubject : {}),
          case_brief: json.brief,
          case_brief_generated_at: json.brief.generated_at,
        }))
        if (refreshedSubject?.kind === 'lead') {
          setProfileData({ tp: refreshedSubject.tp || null, yp: refreshedSubject.yp || null })
          setEmail(refreshedSubject.email || null)
          setPhone(refreshedSubject.phone || null)
          setTriagePublicToken(refreshedSubject.triage_public_token || null)
        }
        setViewedBrief(null)
        setViewedSessionId(null)
      }

      if (!refreshedSubject && !profileData.yp && json.brief.tax_year) {
        const refreshed = await fetchAdvisorSubject()
        if (refreshed.kind === 'lead' && refreshed.yp) {
          setProfileData(prev => ({ ...prev, yp: refreshed.yp }))
        }
      }
    } catch (err) {
      setError(err.message || 'Refresh failed.')
    } finally {
      setGenerating(false)
      setRegenerating(false)
    }
  }

  function viewPublishedSession(sessionId) {
    if (sessionId === null) {
      setViewedSessionId(null)
      setViewedBrief(null)
      return
    }
    setViewedSessionId(sessionId)
    generateBrief(false, sessionId)
  }

  const activeBrief = viewedBrief || subject?.case_brief || null

  const sections = useMemo(() => {
    if (!subject) return null
    if (subject.intake) {
      return buildIntakeSections(subject.intake, {
        name: subject.display_name || subject.intake.contact?.name || 'Unnamed subject',
        email,
        phone,
        leadStatus: subject.lead_status || 'new',
        assignedAt: subject.lead_assigned_at,
      })
    }
    if (!activeBrief) return null
    return buildSections(activeBrief, subject, profileData.tp, profileData.yp, email, phone, triagePublicToken)
  }, [activeBrief, subject, profileData, email, phone, triagePublicToken])

  if (loading) {
    return (
      <div className="brief-page">
        <div className="brief-generating">
          <div className="brief-generating-sub">Loading…</div>
        </div>
      </div>
    )
  }

  if (!subject) {
    return (
      <div className="brief-page">
        <button className="brief-back" onClick={() => navigate('/advisor')}>← Back to dashboard</button>
        <p className="brief-error-text">Subject not found.</p>
      </div>
    )
  }

  if (subject.kind === 'referral_placeholder') {
    return (
      <div className="brief-page">
        <button className="brief-back" onClick={() => navigate('/advisor')}>← Back to dashboard</button>

        <div className="brief-placeholder">
          <div className="brief-placeholder-title">Referral in progress</div>
          {subject.masked_email && (
            <div className="u-name-sub">{subject.masked_email}</div>
          )}
          <p className="brief-placeholder-text">
            This person was referred to your firm
            {subject.source_product ? ` via ${getAdvisorProductLabel(subject.source_product)}` : ''},
            but hasn't yet agreed to share their tax details with you. No name, contact
            information, or tax facts are available until they do.
          </p>
          <div className="brief-header-right-row">
            <span className="brief-header-right-label">Pipeline:</span>
            {statusLabel(subject.lead_status)}
          </div>
          <div className="brief-header-right-row">
            <span className="brief-header-right-label">Assigned:</span>
            {formatDate(subject.lead_assigned_at)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="brief-page">
      <button className="brief-back" onClick={() => navigate('/advisor')}>← Back to dashboard</button>

      {error && (
        <div className="brief-error">
          <span>{error}</span>
          <button className="brief-error-retry" onClick={() => generateBrief(false)}>Retry</button>
        </div>
      )}

      {generating && !subject.case_brief && (
        <div className="brief-generating">
          <div className="brief-generating-title">{subject.display_name}</div>
          <div className="brief-generating-dots">
            {[0,1,2].map(i => (
              <div key={i} className="brief-generating-dot" style={{ animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
          <div className="brief-generating-sub">Loading profile facts…</div>
        </div>
      )}

      {sections && (
        <>
          <CaseHeader
            header={sections.header}
            generatedAt={subject.case_brief_generated_at}
            onRegenerate={() => generateBrief(true)}
            regenerating={regenerating}
          />

          {sections.intake && <GapsCard gaps={sections.intake.gaps} />}

          {viewedSessionId && (
            <div className="brief-peek-note">
              Showing the estimate and data conflicts from a different scenario below — everything else on this page reflects the confirmed profile and doesn't change based on which scenario you're viewing.
            </div>
          )}

          <div className="brief-columns">
            <IncomeColumn
              sections={sections}
              visibleSessions={subject.visible_sessions}
              viewedSessionId={viewedSessionId}
              onSelectSession={viewPublishedSession}
              loadingSession={generating}
            />
            <AssetsColumn sections={sections} />
          </div>

          <TaxWorkSection
            setupTasks={sections.setupTasks}
            spainFindings={sections.spainFindings}
            unconfirmedFacts={sections.unconfirmedFacts}
            unconfirmedLabel={sections.intake ? ADVISOR_INTAKE_UNCONFIRMED_LABEL : undefined}
            notComputedNote={sections.taxWorkComputed ? null : ADVISOR_INTAKE_NO_TAX_WORK}
          />

          {sections.advisorNotes && (
            <BriefSection title="Client notes">
              <p className="brief-advisor-text">{sections.advisorNotes}</p>
            </BriefSection>
          )}

          {sections.intake && <CaveatsSection caveats={sections.intake.caveats} />}
        </>
      )}
    </div>
  )
}